import React, { useEffect, useRef, useState, useMemo } from 'react';
import gsap from 'gsap';
import { useSound } from '../hooks/useSound';
import { Confetti } from '../lib/animations';
import { GameReview } from './reviews/GameReview';
import { ReviewPlayer } from './ReviewPlayer';
import { Trophy, Star, Target, Zap, RotateCcw, ArrowLeft, Home, BookOpen, ChevronDown, ChevronUp, CheckCircle, XCircle, MinusCircle, Play } from 'lucide-react';

const GAME_LABELS = {
  la_course: { name: 'La Course', scoreLabel: 'Score' },
  la_tour: { name: 'La Tour', scoreLabel: 'Tower Height' },
  memoire: { name: 'La Memoire', scoreLabel: 'Pairs Found' },
  avalanche: { name: "L'Avalanche", scoreLabel: 'Words Caught' },
  duel_conjugaison: { name: 'Le Duel', scoreLabel: 'Goals' },
  tri_express: { name: 'Tri Express', scoreLabel: 'Sorted' },
  batisseur: { name: 'Batisseur', scoreLabel: 'Sentences Built' },
  robot_balayeur: { name: 'Robot Balayeur', scoreLabel: 'Sets Read' },
  horloge_folle: { name: "L'Horloge Folle", scoreLabel: 'Correct' },
  detective: { name: 'Le Detective', scoreLabel: 'Clues Solved' },
  carte_du_canada: { name: 'Carte du Canada', scoreLabel: 'Locations Found' },
  le_chef: { name: 'Le Chef', scoreLabel: 'Steps Completed' },
};

export function EndScreen({ results, settings, gameName, items, onPlayAgain, onNewGame, onExit }) {
  const { playSound, playGameMusic, stopMusic } = useSound();
  const [reviewMode, setReviewMode] = useState(false);

  // Play calm end-screen music on mount, stop on unmount
  useEffect(() => { playGameMusic('endscreen'); return () => stopMusic(); }, [playGameMusic, stopMusic]);
  
  // All hooks must be called before any early returns (React rules of hooks)
  const containerRef = useRef(null);
  const s1Ref = useRef(null);
  const s2Ref = useRef(null);
  const soloRef = useRef(null);
  
  const {
    team1Score = 0, team2Score = 0,
    totalQuestions = 0, accuracy = 0, bestStreak = 0,
  } = results || {};

  // Determine if this was actually a team game (both scores > 0 or participation is team mode)
  const isTeam = ['teams', 'head_to_head'].includes(settings.participation) && team2Score !== undefined && (team1Score > 0 || team2Score > 0);

  const gameInfo = GAME_LABELS[gameName] || { name: gameName, scoreLabel: 'Score' };
  const winner = team1Score > team2Score ? settings.team1Name : team1Score < team2Score ? settings.team2Name : null;
  const winnerColor = team1Score > team2Score ? 'var(--team1)' : 'var(--team2)';

  const hasDuelReview = !!results?.questionReview && results.questionReview.length > 0 && !results?.reviewType && totalQuestions > 0;
  const hasAnyReview = ((!!results?.questionReview && results.questionReview.length > 0) || !!results?.reviewType) && totalQuestions > 0;

  useEffect(() => {
    // Don't run animations when in review mode
    if (reviewMode) return;
    
    playSound('win');
    if (!containerRef.current) return;
    const tl = gsap.timeline();
    tl.fromTo(containerRef.current.children,
      { y: 30, opacity: 0, scale: 0.9 },
      { y: 0, opacity: 1, scale: 1, duration: 0.4, stagger: 0.1, ease: 'back.out(1.7)' }
    );
    // Animate score counting
    if (isTeam && s1Ref.current && s2Ref.current) {
      const o1 = { v: 0 }, o2 = { v: 0 };
      tl.to(o1, { v: team1Score, duration: 0.8, ease: 'power2.out', onUpdate: () => { if (s1Ref.current) s1Ref.current.textContent = Math.round(o1.v); } }, '-=0.2');
      tl.to(o2, { v: team2Score, duration: 0.8, ease: 'power2.out', onUpdate: () => { if (s2Ref.current) s2Ref.current.textContent = Math.round(o2.v); } }, '<');
    } else if (soloRef.current) {
      const o = { v: 0 };
      tl.to(o, { v: team1Score, duration: 0.8, ease: 'power2.out', onUpdate: () => { if (soloRef.current) soloRef.current.textContent = Math.round(o.v); } }, '-=0.2');
    }
  }, [reviewMode]);

  // If in review mode, show the ReviewPlayer
  if (reviewMode) {
    return <ReviewPlayer items={items} settings={settings} gameName={gameName} onExit={() => setReviewMode(false)} />;
  }

  return (
    <div data-testid="end-screen" style={{
      height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
      background: 'linear-gradient(180deg, #FFF9DB 0%, #F0F4F8 100%)', padding: '1.5rem 2rem',
      overflowY: 'auto',
    }}>
      <Confetti trigger={1} />
      <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: hasAnyReview ? '0.75rem' : '1.25rem', maxWidth: 950, width: '100%' }}>

        {isTeam && hasDuelReview ? (
          /* ===== DUEL COMPACT HEADER: Score | Winner | Score ===== */
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '2rem', width: '100%', marginBottom: '0.25rem',
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 700, color: 'var(--team1)' }}>{settings.team1Name}</div>
              <div ref={s1Ref} className="font-score" style={{ fontSize: '3.5rem', fontWeight: 800, color: 'var(--team1)', lineHeight: 1 }}>0</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Trophy size={36} style={{ color: 'var(--accent)', marginBottom: '0.15rem' }} />
              <h1 style={{ fontFamily: 'Fredoka', fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', fontWeight: 700, color: winner ? winnerColor : 'var(--accent)', lineHeight: 1.1 }}>
                {winner ? `${winner} Wins!` : 'Tie Game!'}
              </h1>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'Fredoka', fontSize: '1rem', fontWeight: 700, color: 'var(--team2)' }}>{settings.team2Name}</div>
              <div ref={s2Ref} className="font-score" style={{ fontSize: '3.5rem', fontWeight: 800, color: 'var(--team2)', lineHeight: 1 }}>0</div>
            </div>
          </div>
        ) : isTeam ? (
          /* ===== TEAM RESULTS (non-duel) ===== */
          <>
            <div style={{ fontSize: '1rem', color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {gameInfo.name}
            </div>
            <Trophy size={60} style={{ color: 'var(--accent)' }} />
            <h1 style={{ fontFamily: 'Fredoka', fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, color: winner ? winnerColor : 'var(--accent)', textAlign: 'center' }}>
              {winner ? `${winner} Wins!` : 'Tie Game!'}
            </h1>
            <div style={{ display: 'flex', gap: '2.5rem', alignItems: 'center' }}>
              <TeamScore name={settings.team1Name} score={0} color="var(--team1)" scoreRef={s1Ref} />
              <span style={{ fontSize: '1.25rem', color: 'var(--text-muted)', fontWeight: 700 }}>vs</span>
              <TeamScore name={settings.team2Name} score={0} color="var(--team2)" scoreRef={s2Ref} />
            </div>
          </>
        ) : (
          /* ===== SOLO RESULTS ===== */
          <>
            <div style={{ fontSize: '1rem', color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {gameInfo.name}
            </div>
            <Trophy size={60} style={{ color: 'var(--accent)' }} />
            <h1 style={{ fontFamily: 'Fredoka', fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, color: 'var(--primary)', textAlign: 'center' }}>
              {team1Score > 0 ? 'Great Job!' : 'Game Complete'}
            </h1>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '0.25rem' }}>
                {gameInfo.scoreLabel}
              </div>
              <div ref={soloRef} className="font-score" style={{ fontSize: '5rem', fontWeight: 700, color: 'var(--primary)', lineHeight: 1 }}>
                0
              </div>
            </div>
          </>
        )}

        {/* Stats row — hide when there's a question review (the review IS the content) */}
        {!hasAnyReview && (
          <div style={{
            display: 'flex', gap: '1.5rem', flexWrap: 'wrap', justifyContent: 'center',
            background: 'var(--surface)', borderRadius: '1rem', padding: '0.75rem 1.5rem',
            border: '2px solid var(--border)', boxShadow: '0 2px 6px var(--shadow)',
          }}>
            {totalQuestions > 0 && (
              <StatItem icon={<Target size={16} />} label="Questions" value={totalQuestions} />
            )}
            {accuracy > 0 && (
              <StatItem icon={<Star size={16} />} label="Accuracy" value={`${Math.round(accuracy)}%`} />
            )}
            {bestStreak > 1 && (
              <StatItem icon={<Zap size={16} />} label="Best Streak" value={bestStreak} />
            )}
            {totalQuestions === 0 && accuracy === 0 && (
              <div style={{ fontSize: '1rem', color: 'var(--text-muted)', padding: '0.5rem' }}>
                No stats available
              </div>
            )}
          </div>
        )}

        {/* Question Review — Duel-specific (backward compat: no reviewType) */}
        {results?.questionReview && results.questionReview.length > 0 && !results.reviewType && totalQuestions > 0 && (
          <DuelQuestionReview review={results.questionReview} team1Name={settings.team1Name} team2Name={settings.team2Name} />
        )}

        {/* Generic Question Review — all other games */}
        {results?.reviewType && results?.questionReview && results.questionReview.length > 0 && totalQuestions > 0 && (
          <GameReview reviewType={results.reviewType} reviewData={results.questionReview} gameName={gameName} settings={settings} />
        )}

        {/* Curriculum Summary — only when there's actual practice to summarise */}
        {items && items.length > 0 && totalQuestions > 0 && (
          <SessionCurriculumSummary items={items} />
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center', marginTop: '0.5rem' }}>
          {totalQuestions > 0 && (
            <button data-testid="review-answers-button" className="btn-arcade" onClick={() => setReviewMode(true)}
              style={{ gap: '0.5rem', background: 'var(--success)', borderBottomColor: '#2B8A3E', color: 'white' }}>
              <Play size={20} /> Review with Answers
            </button>
          )}
          <button data-testid="play-again-button" className="btn-arcade btn-success" onClick={onPlayAgain} style={{ gap: '0.5rem' }}>
            <RotateCcw size={20} /> Play Again
          </button>
          <button data-testid="new-game-button" className="btn-arcade btn-primary" onClick={onNewGame} style={{ gap: '0.5rem' }}>
            <ArrowLeft size={20} /> New Game
          </button>
          <button data-testid="exit-button" className="btn-arcade btn-surface" onClick={onExit} style={{ gap: '0.5rem' }}>
            <Home size={20} /> Exit
          </button>
        </div>
      </div>
    </div>
  );
}

function TeamScore({ name, score, color, scoreRef }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontFamily: 'Fredoka', fontSize: '1.1rem', color, fontWeight: 700 }}>{name}</div>
      <div ref={scoreRef} className="font-score" style={{ fontSize: '4rem', fontWeight: 700, color, lineHeight: 1 }}>{score}</div>
    </div>
  );
}

function StatItem({ icon, label, value }) {
  return (
    <div style={{ textAlign: 'center', minWidth: 70 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', justifyContent: 'center', fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>
        {icon} {label}
      </div>
      <div className="font-score" style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)' }}>{value}</div>
    </div>
  );
}


function DuelQuestionReview({ review, team1Name, team2Name }) {
  const [expanded, setExpanded] = useState(true);

  const t1Questions = review.filter(q => q.team === 1);
  const t2Questions = review.filter(q => q.team === 2);
  const maxLen = Math.max(t1Questions.length, t2Questions.length);

  return (
    <div data-testid="duel-question-review" style={{
      width: '100%', maxWidth: 900,
      background: 'var(--surface)', border: '2px solid var(--border)',
      borderRadius: '1.25rem', overflow: 'hidden',
    }}>
      <button data-testid="question-review-toggle" onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%', padding: '1rem 1.25rem', background: 'none', border: 'none',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <BookOpen size={22} color="var(--primary)" />
          <span style={{ fontFamily: 'Fredoka', fontWeight: 700, fontSize: '1.3rem', color: 'var(--text-primary)' }}>
            Question Review
          </span>
          <span style={{ fontSize: '1rem', color: 'var(--text-muted)', fontWeight: 600 }}>
            {review.length} questions
          </span>
        </div>
        {expanded ? <ChevronUp size={22} color="var(--text-muted)" /> : <ChevronDown size={22} color="var(--text-muted)" />}
      </button>

      {expanded && (
        <div style={{ padding: '0 1rem 1.25rem', maxHeight: '50vh', overflowY: 'auto' }}>
          {/* Column headers */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.75rem',
            position: 'sticky', top: 0, zIndex: 2, paddingBottom: '0.25rem',
          }}>
            <div style={{
              fontFamily: 'Fredoka', fontSize: '1.15rem', fontWeight: 800,
              color: 'white', textAlign: 'center',
              padding: '0.5rem', background: 'var(--team1)', borderRadius: '0.75rem',
            }}>{team1Name}</div>
            <div style={{
              fontFamily: 'Fredoka', fontSize: '1.15rem', fontWeight: 800,
              color: 'white', textAlign: 'center',
              padding: '0.5rem', background: 'var(--team2)', borderRadius: '0.75rem',
            }}>{team2Name}</div>
          </div>

          {/* Questions side by side */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {Array.from({ length: maxLen }).map((_, i) => (
              <React.Fragment key={i}>
                <ReviewCard q={t1Questions[i]} color="var(--team1)" />
                <ReviewCard q={t2Questions[i]} color="var(--team2)" />
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewCard({ q, color }) {
  if (!q) return <div />;
  const isCorrect = q.wasCorrect === true;
  const isWrong = q.wasCorrect === false;
  const notAttempted = q.wasCorrect === null;

  return (
    <div style={{
      background: isCorrect ? 'rgba(47,158,68,0.08)' : isWrong ? 'rgba(224,49,49,0.08)' : 'rgba(0,0,0,0.03)',
      borderRadius: '1rem', padding: '0.85rem 1rem',
      border: `2px solid ${isCorrect ? 'rgba(47,158,68,0.2)' : isWrong ? 'rgba(224,49,49,0.2)' : 'rgba(0,0,0,0.06)'}`,
    }}>
      {/* Status + Question */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
        {isCorrect && <CheckCircle size={22} color="var(--success)" />}
        {isWrong && <XCircle size={22} color="var(--error)" />}
        {notAttempted && <MinusCircle size={22} color="#aaa" />}
        <span style={{
          fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700,
          color: 'var(--text-primary)',
        }}>
          {q.prompt}
        </span>
      </div>

      {/* Correct answer — always big and green */}
      <div style={{
        fontFamily: 'Fredoka', fontSize: '1.5rem', fontWeight: 800,
        color: 'var(--success)', marginLeft: '2.1rem',
      }}>
        {q.correctAnswer}
      </div>

      {notAttempted && (
        <div style={{
          fontSize: '0.85rem', color: '#999', fontWeight: 600,
          marginLeft: '1.9rem', marginTop: '0.1rem',
        }}>
          Not reached
        </div>
      )}
    </div>
  );
}


const ONTARIO_CODE_LOOKUP = {
  'A1.1': 'Using Listening Comprehension Strategies',
  'A1.2': 'Demonstrating Understanding',
  'A2.1': 'Using Interactive Listening Strategies',
  'A2.2': 'Interacting',
  'A2.3': 'Metacognition',
  'A3.1': 'Intercultural Awareness',
  'A3.2': 'Awareness of Sociolinguistic Conventions',
  'B1.1': 'Using Oral Communication Strategies',
  'B1.2': 'Producing Oral Communications',
  'B1.3': 'Speaking with Fluency',
  'B2.1': 'Using Interactive Speaking Strategies',
  'B2.2': 'Interacting',
  'B2.3': 'Metacognition',
  'B3.1': 'Intercultural Awareness',
  'B3.2': 'Awareness of Sociolinguistic Conventions',
  'C1.1': 'Using Reading Comprehension Strategies',
  'C1.2': 'Reading for Meaning',
  'C1.3': 'Reading with Fluency',
  'C1.4': 'Developing Vocabulary',
  'C2.1': 'Purpose, Form, and Style',
  'C2.2': 'Exploring Text Forms',
  'C2.3': 'Metacognition',
  'C3.1': 'Intercultural Awareness',
  'C3.2': 'Awareness of Sociolinguistic Conventions',
  'D1.1': 'Identifying Purpose and Audience',
  'D1.2': 'Writing in a Variety of Forms',
  'D1.4': 'Developing Vocabulary',
  'D2.1': 'Generating, Developing, Organizing Ideas',
  'D2.2': 'Drafting and Revising',
  'D2.3': 'Producing Finished Work',
  'D2.4': 'Metacognition',
  'D3.1': 'Intercultural Awareness',
  'D3.2': 'Awareness of Sociolinguistic Conventions',
};

const STRAND_NAMES = { A: 'Listening', B: 'Speaking', C: 'Reading', D: 'Writing' };
const STRAND_COLORS = { A: '#4C6EF5', B: '#E03131', C: '#2F9E44', D: '#F59F00' };

function SessionCurriculumSummary({ items }) {
  const [expanded, setExpanded] = useState(false);

  const summary = useMemo(() => {
    const ontCounts = {};
    const bcCodes = new Set();
    const cefrCounts = {};

    for (const item of items) {
      const tags = item.curriculum_tags || {};
      const ont = tags.ontario_expectation;
      const bc = tags.bc_alignment;
      const cefr = tags.cefr;

      if (ont) ontCounts[ont] = (ontCounts[ont] || 0) + 1;
      if (bc) bcCodes.add(bc);
      if (cefr) cefrCounts[cefr] = (cefrCounts[cefr] || 0) + 1;
    }

    // Sort Ontario by count descending
    const ontEntries = Object.entries(ontCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([code, count]) => ({
        code,
        strand: STRAND_NAMES[code[0]] || '',
        strandColor: STRAND_COLORS[code[0]] || '#868E96',
        title: ONTARIO_CODE_LOOKUP[code] || code,
        count,
      }));

    // Parse BC codes to readable labels
    const bcLabels = [...bcCodes].map(code => {
      const rest = code.replace(/^BC\d+_/, '');
      return rest.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    });

    // CEFR summary
    const cefrOrder = ['pre-A1', 'A1', 'A1-A2', 'A2'];
    const cefrList = cefrOrder.filter(l => cefrCounts[l]).map(l => ({ level: l, count: cefrCounts[l] }));

    return { ontEntries, bcLabels, cefrList, totalItems: items.length };
  }, [items]);

  if (summary.ontEntries.length === 0) return null;

  return (
    <div
      data-testid="session-curriculum-summary"
      style={{
        width: '100%',
        maxWidth: 500,
        background: 'var(--surface)',
        border: '2px solid var(--border)',
        borderRadius: '1rem',
        overflow: 'hidden',
      }}
    >
      {/* Collapsed summary header */}
      <button
        data-testid="curriculum-summary-toggle"
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          padding: '0.75rem 1rem',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <BookOpen size={16} color="var(--primary)" />
          <span style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
            Curriculum Content Practiced
          </span>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 500 }}>
            {summary.ontEntries.length} Ontario &middot; {summary.cefrList.map(c => c.level).join(', ')}
          </span>
        </div>
        {expanded ? <ChevronUp size={16} color="var(--text-muted)" /> : <ChevronDown size={16} color="var(--text-muted)" />}
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ padding: '0 1rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {/* Ontario expectations */}
          <div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.35rem', letterSpacing: '0.05em' }}>
              Ontario Curriculum
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
              {summary.ontEntries.map(e => (
                <div key={e.code} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}>
                  <span style={{
                    background: e.strandColor,
                    color: 'white',
                    padding: '0.1rem 0.35rem',
                    borderRadius: '0.25rem',
                    fontFamily: 'Fredoka, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    flexShrink: 0,
                  }}>
                    {e.code}
                  </span>
                  <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>
                    {e.title}
                  </span>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', marginLeft: 'auto', flexShrink: 0 }}>
                    {e.count} items
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* BC alignment */}
          {summary.bcLabels.length > 0 && (
            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.35rem', letterSpacing: '0.05em' }}>
                BC Curriculum
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                {summary.bcLabels.map(label => (
                  <span key={label} style={{
                    background: 'var(--surface-el)',
                    borderRadius: '0.35rem',
                    padding: '0.2rem 0.45rem',
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)',
                    fontWeight: 500,
                  }}>
                    {label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* CEFR */}
          <div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.35rem', letterSpacing: '0.05em' }}>
              CEFR Levels
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {summary.cefrList.map(c => (
                <span key={c.level} style={{
                  background: c.level === 'pre-A1' ? '#E6FCF5' : c.level === 'A1' ? '#E7F5FF' : c.level === 'A1-A2' ? '#FFF9DB' : '#FFF5F5',
                  borderRadius: '0.35rem',
                  padding: '0.2rem 0.5rem',
                  fontSize: '0.8rem',
                  fontFamily: 'Fredoka, sans-serif',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                }}>
                  {c.level}: {c.count}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
